import { ChevronRight } from "lucide-react";
import type { GatewayProvider } from "../../../hooks/gateway/useGatewayProviders";
import type { GatewayConnectionType } from "../../../hooks/requests/gym";

interface ProviderCardProps {
  provider: GatewayProvider;
  configured: boolean;
  configuredAt?: string;
  configuredBy?: string;
  connectionType?: GatewayConnectionType;
  onConfigure: () => void;
}

export const ProviderCard = ({
  provider,
  configured,
  configuredAt,
  configuredBy,
  connectionType,
  onConfigure,
}: ProviderCardProps) => {
  const isComingSoon = !provider.available;
  const ctaLabel = provider.oauthAvailable
    ? `Conectar con ${provider.displayName}`
    : "Configurar";
  const connectedLabel =
    connectionType === "oauth"
      ? `Conectado vía ${provider.displayName}`
      : "Conectado";

  return (
    <button
      type="button"
      onClick={isComingSoon ? undefined : onConfigure}
      disabled={isComingSoon}
      className={`card-compact w-full text-left transition-all duration-300 ${
        isComingSoon
          ? "cursor-default opacity-40"
          : "cursor-pointer hover:border-global-accent/30"
      }`}
    >
      <div className="flex flex-col items-center gap-3 py-2">
        <div className="w-full h-24 rounded-2xl bg-white border border-global-secondary flex items-center justify-center px-6 py-4 mx-2">
          <img
            src={provider.logo}
            alt={provider.displayName}
            className="max-w-full max-h-full object-contain"
          />
        </div>

        <div className="text-center w-full">
          <div className="flex items-center justify-center gap-1.5">
            <p className="text-size-sm font-semibold text-global-title">
              {provider.displayName}
            </p>
          </div>
          <p className="text-size-2xs font-medium text-global-accent mt-0.5">
            {provider.productName}
          </p>
          <p className="text-size-2xs text-global-muted mt-1 line-clamp-2">
            {provider.description}
          </p>
          {provider.commission && (
            <p className="text-size-2xs text-global-tertiary mt-1.5 line-clamp-2">
              Comision: {provider.commission}
            </p>
          )}
        </div>

        <div className="w-full pt-2 border-t border-global-secondary">
          {isComingSoon ? (
            <div className="flex justify-center">
              <span className="text-size-2xs font-medium px-2.5 py-1 rounded-md bg-global-info text-global-muted">
                Proximamente
              </span>
            </div>
          ) : configured ? (
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[var(--color-success-text)]" />
                <span className="text-size-2xs font-semibold text-global-success">
                  {connectedLabel}
                </span>
              </div>
              {configuredAt && (
                <p className="text-size-2xs text-global-muted text-center">
                  {configuredBy ? `${configuredBy}` : ""} ·{" "}
                  {new Date(configuredAt).toLocaleDateString("es-CL")}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1 text-global-accent">
              <span className="text-size-2xs font-semibold">{ctaLabel}</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};
